import { useRef, useState } from 'react';
import { submitLabCheckout, refreshPaymentConfig } from '../services/labApi';
import { normalizeLabPaymentPayload, clearLabIdempotencyKey } from '../services/labPaymentAdapter';
import { buildLabCheckoutPayload, mapLabSubmitError, validateStep3Collection } from '../services/labCheckoutFlow';

/**
 * Owns the final order submission flow: validation guards, payment data
 * collection, building the checkout payload, calling the API, and mapping
 * success / failure into UI state.
 *
 * All wizard values are passed in fresh each render so the returned
 * handlePlaceOrder closes over current state.
 */
export default function useLabCheckoutSubmit(ctx) {
  const {
    slug, lab, patientId, dob, sex, phone, address, zip,
    selectedMethod, selectedDrawSite, availResult,
    checkoutAoeQuestions, aoeAnswers, missingAoeQuestions,
    consentHipaa, consentTerms, consentTele,
    checkoutPoliciesMissing, total, appliedCoupon, paymentStepRef,
    setFormError, clearSession, clearAuthenticatedState, setDetailsMode, setStep,
  } = ctx;

  const submitInFlightRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState(null);

  const handlePlaceOrder = async () => {
    if (submitInFlightRef.current) return;
    setFormError('');
    if (!patientId) { setFormError('Please sign in or create an account in Step 2.'); return; }
    if (!dob || !sex) { setFormError('Please complete patient details in Step 2.'); return; }
    const step3Validation = validateStep3Collection({
      selectedMethod,
      availResult,
      address,
      selectedDrawSite,
      zip,
    });
    if (!step3Validation.isValid) {
      setFormError(step3Validation.error || 'Please complete collection setup in Step 3.');
      return;
    }
    if (missingAoeQuestions.length > 0) {
      setFormError('Please answer all required lab questions.');
      return;
    }
    if (!consentHipaa || !consentTerms || !consentTele) { setFormError('You must agree to all three consents.'); return; }
    if (checkoutPoliciesMissing.length > 0) { setFormError('Required checkout policies are unavailable. Please try again later.'); return; }

    const paymentRequired = total > 0;
    submitInFlightRef.current = true;
    setIsSubmitting(true);
    try {
      let paymentData = null;
      if (paymentRequired && paymentStepRef.current) {
        const rawPayment = await paymentStepRef.current.getPaymentData();
        paymentData = normalizeLabPaymentPayload(rawPayment, { slug, patientId, total });
        if (!paymentData) {
          setFormError('Please complete your payment details.');
          return;
        }
        const activeGateway = await refreshPaymentConfig();
        const normalizedActiveGateway = activeGateway === 'authorizenet' ? 'authorize_net' : activeGateway;
        if (normalizedActiveGateway && normalizedActiveGateway !== paymentData.processor) {
          setFormError('The accepted payment method has recently changed. Please refresh the page and re-enter your payment details.');
          return;
        }
      } else if (!paymentRequired) {
        paymentData = { processor: 'zero_total', amount: 0 };
      }

      const payload = buildLabCheckoutPayload({
        lab, patientId, dob, sex, phone, address, zip,
        selectedMethod, selectedDrawSite,
        aoeQuestions: checkoutAoeQuestions, aoeAnswers,
        consentHipaa, consentTerms, consentTele,
        paymentData, appliedCoupon, total,
        availabilityFingerprint: availResult?.policy_fingerprint,
      });
      const result = await submitLabCheckout(slug, payload);
      if (result.success) {
        clearLabIdempotencyKey({ slug, patientId, total });
        if (typeof clearSession === 'function') clearSession();
        setConfirmedOrder({ state: 'success', orderNumber: result.orderNumber, message: result.message, total });
        window.scrollTo(0, 0);
      } else {
        setFormError(result.message || 'Checkout failed.');
      }
    } catch (err) {
      const mapped = mapLabSubmitError(err);
      if (mapped.confirmedOrder) {
        setConfirmedOrder(mapped.confirmedOrder);
        window.scrollTo(0, 0);
      } else if (mapped.sessionExpired) {
        localStorage.removeItem('patient_access_token');
        sessionStorage.removeItem('patient_access_token');
        if (typeof clearAuthenticatedState === 'function') clearAuthenticatedState();
        setDetailsMode('account');
        setStep(2);
        setFormError(mapped.message);
      } else {
        setFormError(mapped.message);
      }
    } finally {
      setIsSubmitting(false);
      submitInFlightRef.current = false;
    }
  };

  return { isSubmitting, confirmedOrder, handlePlaceOrder };
}
